import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Printer, Pill, CalendarCheck, Activity, Lightbulb, Heart, Apple } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSummary } from "@/lib/api";

export default function PatientSummary() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getSummary(id);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load summary");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="summary-loading">
        <div className="animate-pulse space-y-4 w-full max-w-2xl p-8">
          <div className="h-8 bg-slate-200 rounded w-1/2" />
          <div className="h-4 bg-slate-100 rounded w-3/4" />
          <div className="h-40 bg-slate-100 rounded-xl" />
          <div className="h-40 bg-slate-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="summary-error">
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-md">
          <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h2 className="font-heading font-semibold text-slate-800 text-lg mb-2">Summary Not Available</h2>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <Button variant="outline" asChild>
            <Link to={`/patients/${id}`}><ArrowLeft className="w-4 h-4 mr-2" />Back to Patient</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-summary-page">
      {/* Nav bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Button variant="ghost" size="sm" asChild data-testid="summary-back-btn">
            <Link to={`/patients/${id}`}><ArrowLeft className="w-4 h-4 mr-2" />Back</Link>
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-brand-500 flex items-center justify-center">
              <Activity className="w-3 h-3 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-semibold text-slate-900 text-sm">Discharge IQ</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-summary-btn">
            <Printer className="w-4 h-4 mr-2" />Print
          </Button>
        </div>
      </header>

      {/* Summary Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8">
        {/* Patient Header */}
        <div className="bg-white border-0 shadow-[0_4px_24px_rgba(0,0,0,0.04)] rounded-xl p-8 sm:p-10" data-testid="summary-header">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-600 mb-2">Discharge Summary</p>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 tracking-tight" data-testid="summary-patient-name">
            {data.patient_name}
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-mono" data-testid="summary-mir">MIR-{data.mir_number}</p>
          {data.generated_at && (
            <p className="text-xs text-slate-400 mt-3">
              Generated on {format(new Date(data.generated_at), "MMMM d, yyyy 'at' h:mm a")}
            </p>
          )}
        </div>

        {/* Stay Description */}
        <section className="bg-white border-0 shadow-[0_4px_24px_rgba(0,0,0,0.04)] rounded-xl p-8 sm:p-10" data-testid="stay-description-section">
          <h2 className="text-xl font-heading font-semibold text-slate-900 mb-4">Hospital Stay Summary</h2>
          <div className="prose prose-slate prose-sm max-w-none">
            {data.stayDescription?.split("\n").map((para, i) => (
              <p key={i} className="text-sm leading-relaxed text-slate-600 mb-3">{para}</p>
            ))}
          </div>
        </section>

        {/* Patient-Friendly Summary */}
        <section className="bg-brand-50 border border-brand-100 rounded-xl p-8 sm:p-10" data-testid="friendly-summary-section">
          <h2 className="text-xl font-heading font-semibold text-brand-900 mb-4">What This Means For You</h2>
          <div className="prose prose-sm max-w-none">
            {data.patientFriendlySummary?.split("\n").map((para, i) => (
              <p key={i} className="text-sm leading-relaxed text-brand-800 mb-3">{para}</p>
            ))}
          </div>
        </section>

        {/* AI-Generated Recommendations */}
        {data.recommendations && (
          <section className="bg-white border-0 shadow-[0_4px_24px_rgba(0,0,0,0.04)] rounded-xl p-8 sm:p-10" data-testid="recommendations-section">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Personalized Recommendations</h2>
            </div>
            <div className="prose prose-slate prose-sm max-w-none">
              {data.recommendations.split("\n").map((para, i) => (
                <p key={i} className="text-sm leading-relaxed text-slate-600 mb-3">{para}</p>
              ))}
            </div>
          </section>
        )}

        {/* AI-Generated Lifestyle Tips */}
        {data.lifestyleTips && (
          <section className="bg-white border-0 shadow-[0_4px_24px_rgba(0,0,0,0.04)] rounded-xl p-8 sm:p-10" data-testid="lifestyle-tips-section">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Lifestyle Tips</h2>
            </div>
            <div className="prose prose-slate prose-sm max-w-none">
              {data.lifestyleTips.split("\n").map((para, i) => (
                <p key={i} className="text-sm leading-relaxed text-slate-600 mb-3">{para}</p>
              ))}
            </div>
          </section>
        )}

        {/* AI-Generated Food Health Tips */}
        {data.foodHealthTips && (
          <section className="bg-white border-0 shadow-[0_4px_24px_rgba(0,0,0,0.04)] rounded-xl p-8 sm:p-10" data-testid="food-health-tips-section">
            <div className="flex items-center gap-2 mb-4">
              <Apple className="w-5 h-5 text-green-600" strokeWidth={1.5} />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Nutrition & Food Health Tips</h2>
            </div>
            <div className="prose prose-slate prose-sm max-w-none">
              {data.foodHealthTips.split("\n").map((para, i) => (
                <p key={i} className="text-sm leading-relaxed text-slate-600 mb-3">{para}</p>
              ))}
            </div>
          </section>
        )}

        {/* Medications */}
        {data.medications?.length > 0 && (
          <section className="bg-white border-0 shadow-[0_4px_24px_rgba(0,0,0,0.04)] rounded-xl p-8 sm:p-10" data-testid="summary-medications">
            <div className="flex items-center gap-2 mb-4">
              <Pill className="w-5 h-5 text-brand-600" strokeWidth={1.5} />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Your Medications</h2>
            </div>
            <div className="space-y-3">
              {data.medications.map((m, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-100" data-testid={`summary-med-${i}`}>
                  <p className="font-medium text-slate-800 text-sm">{m.name}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                    {m.dosage && <span>{m.dosage}</span>}
                    {m.frequency && <span>{m.frequency}</span>}
                  </div>
                  {m.instructions && <p className="text-xs text-slate-600 mt-2 italic">{m.instructions}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Follow-ups */}
        {data.follow_ups?.length > 0 && (
          <section className="bg-white border-0 shadow-[0_4px_24px_rgba(0,0,0,0.04)] rounded-xl p-8 sm:p-10" data-testid="summary-followups">
            <div className="flex items-center gap-2 mb-4">
              <CalendarCheck className="w-5 h-5 text-brand-600" strokeWidth={1.5} />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Follow-up Appointments</h2>
            </div>
            <div className="space-y-3">
              {data.follow_ups.map((f, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-100" data-testid={`summary-followup-${i}`}>
                  <p className="font-medium text-slate-800 text-sm">{f.department || "Department TBD"}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                    {f.physician && <span>with {f.physician}</span>}
                    {f.scheduled_date && <span>on {format(new Date(f.scheduled_date + "T00:00:00"), "MMMM d, yyyy")}</span>}
                  </div>
                  {f.instructions && <p className="text-xs text-slate-600 mt-2 italic">{f.instructions}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Footer */}
        <div className="text-center py-6" data-testid="summary-footer">
          <p className="text-xs text-slate-400">
            This summary was generated by Discharge IQ. Always consult your healthcare provider for medical advice.
          </p>
        </div>
      </main>
    </div>
  );
}
